import {useSyncExternalStore, useMemo} from 'react';

import type {
    ChatMessage,
    DirectChatMessages,
    InMeetingParticipant,
    UnseenUnreadDirectChatMessages,
} from '@pexip/media-components';
import {ChatType, useAssertedContext} from '@pexip/media-components';
import {memoize} from '@pexip/utils';
import type {ParticipantID, RoomID, Participant} from '@pexip/infinity';

import {useConfigContext} from '../config';
import {MeetingContext} from '../contexts/meeting.context';
import type {Meeting} from '../services/InfinityClient.service';
import {
    breakoutsAskingForHelpSignal,
    chatMessagesSignal,
    directChatMessagesSignal,
    guestTransferDetailsSignal,
    hostReferDetailsSignal,
    idpSignal,
    invalidPinSignal,
    layoutOverlayTextEnabledSignal,
    liveCaptionsEnabledSignal,
    selectedCaptionsLanguageSignal,
    isTogglingLiveCaptionsSignal,
    pinRequiredSignal,
    remoteStreamSignal,
    splashScreenSignal,
    stepSignal,
    liveCaptionsSignal,
    unreadChatMessagesSignal,
    unreadDirectChatMessagesSignal,
    unseenUnreadDirectChatMessagesSignal,
} from '../signals/Meeting.signals';
import {infinityClientSignals} from '../signals/InfinityClient.signals';
import {combineSubscribes, type Subscribe} from '../utils/subscribe';
import {
    GroupKey,
    inMeetingParticipantsUpdates,
    toBreakoutGroup,
} from '../utils/createParticipants';
import {participantSearchQuerySignal} from '../signals/InMeeting.signals';
import {applicationConfig} from '../applicationConfig';

export const useMeetingContext = () => useAssertedContext(MeetingContext);

export const onParticipantLeft = combineSubscribes(
    infinityClientSignals.onParticipants.add,
    infinityClientSignals.onParticipantLeft.add,
    infinityClientSignals.onTransfer.add,
    inMeetingParticipantsUpdates.add,
);

export const onBreakoutRoomsChange = combineSubscribes(
    infinityClientSignals.onBreakoutBegin.add,
    infinityClientSignals.onBreakoutEnd.add,
);

export const onRoomChange = combineSubscribes(
    infinityClientSignals.onTransfer.add,
    infinityClientSignals.onBreakoutRefer.add,
);

export const onParticipantsChange = combineSubscribes(
    infinityClientSignals.onParticipants.add,
    infinityClientSignals.onParticipantUpdated.add,
    infinityClientSignals.onParticipantJoined.add,
    infinityClientSignals.onParticipantLeft.add,
    infinityClientSignals.onBreakoutEnd.add,
    inMeetingParticipantsUpdates.add,
);

export const onConferentStatusChange = combineSubscribes(
    infinityClientSignals.onConferenceStatus.add,
    infinityClientSignals.onBreakoutEnd.add,
);

export const useStep = (
    getState: Meeting['getStep'],
    subscribe: Subscribe = stepSignal.add,
) => useSyncExternalStore(subscribe, getState);

export const useRemoteStream = (
    getState: Meeting['getRemoteStream'],
    subscribe: Subscribe = remoteStreamSignal.add,
) => useSyncExternalStore(subscribe, getState);

export const usePinRequired = (
    getState: Meeting['getPinRequired'],
    subscribe: Subscribe = pinRequiredSignal.add,
) => useSyncExternalStore(subscribe, getState);

export const useIdps = (
    getState: Meeting['getIdps'],
    subscribe: Subscribe = idpSignal.add,
) => useSyncExternalStore(subscribe, getState);

export const useLayoutOverlayEnabled = (
    getState: Meeting['getLayoutOverlayEnabled'],
    subscribe: Subscribe = layoutOverlayTextEnabledSignal.add,
) => useSyncExternalStore(subscribe, getState);

export const useSplashScreen = (
    getState: Meeting['getSplashScreen'],
    subscribe: Subscribe = splashScreenSignal.add,
) => useSyncExternalStore(subscribe, getState);

export const useBreakoutsAskingForHelp = (
    subscribe: Subscribe = breakoutsAskingForHelpSignal.add,
) => {
    const meeting = useMeetingContext();
    return useSyncExternalStore(subscribe, meeting.getBreakoutsAskingForHelp);
};

export const useGuestTransferDetails = (
    subscribe: Subscribe = guestTransferDetailsSignal.add,
) => {
    const meeting = useMeetingContext();
    return useSyncExternalStore(subscribe, meeting.getGuestTransferDetails);
};

export const useHostReferDetails = (
    subscribe: Subscribe = hostReferDetailsSignal.add,
) => {
    const meeting = useMeetingContext();
    return useSyncExternalStore(subscribe, meeting.getHostReferDetails);
};

export const useRemoteAlias = (
    subscribe: Subscribe = infinityClientSignals.onAuthenticatedWithConference
        .add,
) => {
    const meeting = useMeetingContext();
    return useSyncExternalStore(subscribe, meeting.getRemoteAlias);
};

export const useInvalidPinFeedback = (
    getInvalidPingMsg: () => string,
    subscribe: Subscribe = invalidPinSignal.add,
) => useSyncExternalStore(subscribe, getInvalidPingMsg);

export const useInvalidPinFeedbackString = () => {
    const meeting = useMeetingContext();
    return useInvalidPinFeedback(() => meeting.getError()?.code ?? '');
};

export const useIsFeccEnabled = (
    getState: Meeting['getIsFeccEnabled'],
    subscribe: Subscribe = onParticipantsChange,
) => useSyncExternalStore(subscribe, getState);

export const useCurrentRoomFeccParticipantIDs = (
    subscribe: Subscribe = onParticipantsChange,
) => {
    const meeting = useMeetingContext();
    const subscribes = combineSubscribes(
        subscribe,
        meeting.getCurrentFeccPidsSignalState().add,
    );
    return useSyncExternalStore(subscribes, () => {
        return meeting.getCurrentRoomFeccParticipantIDs();
    });
};

export const useCurrentHostLayout = (
    getState: Meeting['getCurrentHostLayout'],
    subscribe: Subscribe = infinityClientSignals.onRequestedLayout.add,
) => useSyncExternalStore(subscribe, getState);

export const useEveryoneChatMessages = (
    getState: Meeting['getChatMessages'],
    subscribe: Subscribe = chatMessagesSignal.add,
) => useSyncExternalStore(subscribe, getState);

export const useUnreadChatMessages = (
    getState: Meeting['getUnreadChatMessages'],
    subscribe: Subscribe = unreadChatMessagesSignal.add,
) => useSyncExternalStore(subscribe, getState);

export const useDirectChatMessages = (
    getState: Meeting['getDirectChatMessages'],
    subscribe: Subscribe = directChatMessagesSignal.add,
) => useSyncExternalStore(subscribe, getState);

export const useUnreadDirectChatMessages = (
    getState: Meeting['getUnreadDirectChatMessages'],
    subscribe: Subscribe = unreadDirectChatMessagesSignal.add,
) => useSyncExternalStore(subscribe, getState);

export const useUnseenUnreadDirectChatMessages = (
    getState: Meeting['getUnseenUnreadDirectChatMessages'],
    subscribe: Subscribe = unseenUnreadDirectChatMessagesSignal.add,
) => useSyncExternalStore(subscribe, getState);

export const useIsPresentationInMixActive = (
    getState: Meeting['getIsPresentationInMixActive'],
    subscribe: Subscribe = infinityClientSignals.onLayoutUpdate.add,
) => useSyncExternalStore(subscribe, getState);

export const useAllUnreadAndUnseenChatMessages = (
    unreadChatMessages: ChatMessage[],
    unseenUnreadDirectChatMessages: UnseenUnreadDirectChatMessages,
) =>
    useMemo(
        () => [...unreadChatMessages, ...unseenUnreadDirectChatMessages],
        [unreadChatMessages, unseenUnreadDirectChatMessages],
    );

export const useChatMessages = (
    meeting: ReturnType<typeof useMeetingContext>,
    selectedChatType: ChatType,
    participantInDirectChatWith: Participant | undefined,
) => {
    const everyoneChatMessages = useEveryoneChatMessages(
        meeting.getChatMessages,
    );
    const directChatMessages = useDirectChatMessages(
        meeting.getDirectChatMessages,
    );

    const currentChatMessages = useMemo(
        () =>
            selectedChatType === ChatType.Everyone
                ? everyoneChatMessages
                : participantInDirectChatWith
                  ? (directChatMessages.get(participantInDirectChatWith.uuid) ??
                    [])
                  : [],
        [
            directChatMessages,
            everyoneChatMessages,
            participantInDirectChatWith,
            selectedChatType,
        ],
    );

    return {everyoneChatMessages, directChatMessages, currentChatMessages};
};

export const useParticipantRoomID = (
    participantId?: ParticipantID,
    subscribe: Subscribe = onParticipantsChange,
) => {
    const meeting = useMeetingContext();
    return useSyncExternalStore(subscribe, () =>
        meeting.getParticipantRoomID(participantId),
    );
};

export const useRoomID = (subscribe: Subscribe = onRoomChange) => {
    const meeting = useMeetingContext();
    return useSyncExternalStore(subscribe, meeting.getRoomId);
};

export const useBreakoutParticipants = (
    subscribe: Subscribe = onParticipantsChange,
) => {
    const meeting = useMeetingContext();
    const getParticipants = memoize(
        () => {
            return new Map<RoomID, InMeetingParticipant[]>(
                meeting.getRooms().map(roomId => {
                    const participants: InMeetingParticipant[] = [];
                    for (const pid of meeting.getSupportedBreakoutParticipants({
                        roomId,
                    })) {
                        const participant = meeting.getParticipant(pid, roomId);
                        if (participant) {
                            participants.push(participant);
                        }
                    }
                    return [roomId, participants];
                }),
            );
        },
        () =>
            meeting
                .getRooms()
                .map(roomId =>
                    meeting.getSupportedBreakoutParticipants({roomId}),
                ),
    );
    return useSyncExternalStore(subscribe, getParticipants);
};

export const useBreakoutRoomsSize = (
    subscribe: Subscribe = onBreakoutRoomsChange,
) => {
    const meeting = useMeetingContext();
    return useSyncExternalStore(subscribe, () => meeting.getRooms().length - 1);
};

export const useBreakoutNames = (
    subscribe: Subscribe = onConferentStatusChange,
) => {
    const meeting = useMeetingContext();
    return useSyncExternalStore(subscribe, () =>
        meeting.getBreakoutRoomNames(),
    );
};

export const getParticipantsByGroup = (
    meeting: Meeting,
    filterBy: GroupKey,
    searchQuery?: string,
) => {
    const roomId = meeting.getRoomId();
    const isBreakout = !!meeting.getConferenceStatus().get(roomId)?.breakout;
    const filter = isBreakout ? toBreakoutGroup(filterBy) : filterBy;
    return meeting.getParticipants({roomId, filterBy: filter, searchQuery});
};

export const createParticipantGroupHook =
    (filterBy: GroupKey, applySearch?: boolean) =>
    (
        subscribe = applySearch
            ? combineSubscribes(
                  onParticipantsChange,
                  participantSearchQuerySignal.add,
              )
            : onParticipantsChange,
    ) => {
        const meeting = useMeetingContext();
        return useSyncExternalStore(subscribe, () =>
            getParticipantsByGroup(
                meeting,
                filterBy,
                applySearch === true ? meeting.getSearchQuery() : undefined,
            ),
        );
    };

export const createParticipantGroupSizeHook = (filterBy: GroupKey) => {
    const hook = createParticipantGroupHook(filterBy);
    return () => hook().size;
};

export const createParticipantGroupSizeExcludSelfHook =
    (filterBy: GroupKey) =>
    (subscribe = onParticipantsChange) => {
        const meeting = useMeetingContext();
        return useSyncExternalStore(subscribe, () => {
            const pids = meeting.getParticipants({
                roomId: meeting.getRoomId(),
                filterBy,
            });
            const selfUUID = meeting.getMe()?.uuid;
            if (selfUUID && pids.has(selfUUID)) {
                return pids.size - 1;
            }
            return pids.size;
        });
    };

export const useParticipant = (
    participantId: ParticipantID,
    roomId: RoomID,
    subscribe: Subscribe = combineSubscribes(
        change => {
            return infinityClientSignals.onParticipantUpdated.add(events => {
                if (
                    events.some(evt => {
                        return (
                            roomId === evt.id &&
                            evt.participant.uuid === participantId
                        );
                    })
                ) {
                    change();
                }
            });
        },
        change => {
            return infinityClientSignals.onStage.add(stages => {
                if (stages.some(s => s.userId === participantId)) {
                    change();
                }
            });
        },
    ),
) => {
    const meeting = useMeetingContext();
    return useSyncExternalStore(subscribe, () => {
        return meeting.getParticipant(participantId, roomId);
    });
};

export const useInMeetingParticipants = createParticipantGroupHook(
    GroupKey.InMeeting,
);
export const useInMeetingParticipantsApplySearch = createParticipantGroupHook(
    GroupKey.InMeeting,
    true,
);
export const useInMeetingParticipantsSize = createParticipantGroupSizeHook(
    GroupKey.InMeeting,
);
export const useWaitingInLobbyParticipants = createParticipantGroupHook(
    GroupKey.WaitingInLobby,
);
export const useWaitingInLobbyParticipantsApplySearch =
    createParticipantGroupHook(GroupKey.WaitingInLobby, true);
export const useWaitingInLobbyParticipantsSize = createParticipantGroupSizeHook(
    GroupKey.WaitingInLobby,
);
export const useRaisedHandParticipants = createParticipantGroupHook(
    GroupKey.RaisedHand,
);
export const useRaisedHandParticipantsApplySearch = createParticipantGroupHook(
    GroupKey.RaisedHand,
    true,
);
export const useRaisedHandParticipantsSize = createParticipantGroupSizeHook(
    GroupKey.RaisedHand,
);
export const useRaisedHandParticipantsSizeExcludeSelf =
    createParticipantGroupSizeExcludSelfHook(GroupKey.RaisedHand);
export const useExternalParticipants = createParticipantGroupHook(
    GroupKey.External,
);
export const useExternalParticipantsApplySearch = createParticipantGroupHook(
    GroupKey.External,
    true,
);
export const useExternalParticipantsSize = createParticipantGroupSizeHook(
    GroupKey.External,
);

export const useParticipantDisconnected = (
    participantId: ParticipantID | undefined,
    roomID?: RoomID,

    subscribe: Subscribe = onParticipantLeft,
) => {
    const meeting = useMeetingContext();
    return useSyncExternalStore(subscribe, () => {
        if (!participantId) {
            return true;
        }
        return meeting.isParticipantDisconnected(participantId, roomID);
    });
};

export const useParticipantsGroupByDirectChatSupport = (
    directChatMessages: DirectChatMessages,
    subscribe: Subscribe = onParticipantsChange,
) => {
    const meeting = useMeetingContext();
    const getInMeetingParticipants = memoize(
        () => {
            const supportsChat: ParticipantID[] = [];
            const doesNotSupportChat: ParticipantID[] = [];

            const pids = meeting.getParticipants({
                filterBy: GroupKey.InMeeting,
                roomId: meeting.getRoomId(),
            });
            for (const participantID of pids) {
                if (
                    participantID !== meeting.getMe()?.uuid &&
                    !directChatMessages.has(participantID)
                ) {
                    const participant = meeting.getParticipant(
                        participantID,
                        meeting.getRoomId(),
                    );
                    if (participant?.supportsDirectChat) {
                        supportsChat.push(participantID);
                    } else {
                        doesNotSupportChat.push(participantID);
                    }
                }
            }
            return [supportsChat, doesNotSupportChat] as const;
        },
        () => [
            meeting.getParticipants({
                filterBy: GroupKey.InMeeting,
                roomId: meeting.getRoomId(),
            }),
            meeting.getRoomId(),
        ],
    );

    return useSyncExternalStore(subscribe, getInMeetingParticipants);
};

/**
 * A state to indicate whether the user is capable to send presentation slides
 */
export const useAbleToPresent = () => {
    const config = useConfigContext();
    const meeting = useMeetingContext();
    return useSyncExternalStore(
        onChange => config.subscribe('callType', onChange),
        () => meeting.ableToPresent(),
    );
};

/**
 * A state to indicate whether the participant is capable of sending presentation slides
 */
export const useAllowedToPresent = (
    roomId?: RoomID,
    subscribe = combineSubscribes(
        onConferentStatusChange,
        infinityClientSignals.onMe.add,
    ),
) => {
    const meeting = useMeetingContext();
    return useSyncExternalStore(subscribe, () =>
        meeting.allowedToPresent(roomId),
    );
};

export const useChatEnabled = (
    roomId?: RoomID,
    subscribe = onConferentStatusChange,
) => {
    const meeting = useMeetingContext();
    return useSyncExternalStore(subscribe, () =>
        meeting.getChatEnabled(roomId),
    );
};

export const useLiveCaptions = (
    getState: Meeting['getLiveCaptions'],
    subscribe: Subscribe = liveCaptionsSignal.add,
) => useSyncExternalStore(subscribe, getState);

export const useLiveCaptionsEnabled = (
    getState: Meeting['getLiveCaptionsEnabled'],
    subscribe: Subscribe = liveCaptionsEnabledSignal.add,
) => useSyncExternalStore(subscribe, getState);

export const useSelectedCaptionsLanguage = (
    getState: Meeting['getSelectedCaptionsLanguage'],
    subscribe: Subscribe = selectedCaptionsLanguageSignal.add,
) => useSyncExternalStore(subscribe, getState);

export const useIsTogglingLiveCaptions = (
    getState: Meeting['getIsTogglingLiveCaptions'],
    subscribe: Subscribe = isTogglingLiveCaptionsSignal.add,
) => useSyncExternalStore(subscribe, getState);

export const useLiveCaptionsAvailable = () => {
    const meeting = useMeetingContext();
    const isLiveCaptionsAvailable = useSyncExternalStore(
        infinityClientSignals.onConferenceStatus.add,
        meeting.getLiveCaptionsAvailability,
    );

    return isLiveCaptionsAvailable && applicationConfig.showLiveCaptionsFeature;
};

export const useAvailableCaptionsLanguages = () => {
    const meeting = useMeetingContext();
    return useSyncExternalStore(
        onConferentStatusChange,
        meeting.getAvailableCaptionsLanguages,
    );
};

export const useSourceCaptionsLanguages = () => {
    const meeting = useMeetingContext();
    return useSyncExternalStore(
        onConferentStatusChange,
        meeting.getSourceCaptionsLanguages,
    );
};
